/**
 * Small, dependency-free math helpers.
 *
 * Deliberately does NOT import three.js: everything in the fixed-timestep
 * simulation must be unit-testable in a plain node environment.
 */

export interface Vec2 {
  /** Local movement space: +x = player right. */
  x: number;
  /** Local movement space: +y = player forward. */
  y: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const vec2 = (x = 0, y = 0): Vec2 => ({ x, y });
export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Smooth 0..1 ease used for camera transitions. Pure, no time dependency. */
export function smoothStep(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

export function length2(v: Vec2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

export function normalize2(v: Vec2): Vec2 {
  const len = length2(v);
  if (len < 1e-9) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

/** Returns a copy of `v` with its length limited to `max`. */
export function clampLength2(v: Vec2, max: number): Vec2 {
  const len = length2(v);
  if (len <= max || len < 1e-9) return { x: v.x, y: v.y };
  const s = max / len;
  return { x: v.x * s, y: v.y * s };
}

export function lengthXZ(v: Vec3): number {
  return Math.sqrt(v.x * v.x + v.z * v.z);
}

/**
 * Rotates a local movement vector (x = right, y = forward) into world XZ,
 * matching three.js camera conventions (yaw 0 looks down -Z).
 */
export function localToWorldXZ(local: Vec2, yaw: number): { x: number; z: number } {
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  // right  = ( cos, -sin)
  // forward= (-sin, -cos)
  return {
    x: local.x * cos - local.y * sin,
    z: -local.x * sin - local.y * cos,
  };
}

/** Yaw (three.js convention) that makes the player look from `from` towards `to`. */
export function yawTowards(from: { x: number; z: number }, to: { x: number; z: number }): number {
  return Math.atan2(-(to.x - from.x), -(to.z - from.z));
}

/** Shortest signed angular difference in radians, wrapped to [-PI, PI]. */
export function angleDelta(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
